import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, CardContent, Grid, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import { Icon } from '@iconify/react';
import SplitButton from './SplitButton';
import { CampaignListItemProps } from '../types/apps/Campaign';
import { formatDistanceToNow } from 'date-fns';
import PostContentDialog from '../views/apps/channel/dialogs/PostContentDialog';
import axios from '../services/ApiService';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { deleteCampaign } from '../store/apps/campaign';
function getLastUpdatedFormatted(lastUpdated?: Date | string): string {
    if (typeof lastUpdated === 'string') {
        lastUpdated = new Date(lastUpdated);
    }

    if (!(lastUpdated instanceof Date)) {
        throw new Error('Invalid date format');
    }

    const formattedDate = formatDistanceToNow(lastUpdated, { addSuffix: true });
    return `Updated ${formattedDate}`;
}


function getFormattedDateRange(startDate?: Date, endDate?: Date): string {
    if (startDate && endDate) {

        if (typeof startDate === 'string') {
            startDate = new Date(startDate);
        }

        if (typeof endDate === 'string') {
            endDate = new Date(endDate);
        }

        if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
            throw new Error("Invalid date format");
        }

        const start = `${startDate.getDate()}${getOrdinalIndicator(startDate.getDate())} ${startDate.toLocaleString('default', { month: 'short' })} ${startDate.getFullYear()}`;
        const end = `${endDate.getDate()}${getOrdinalIndicator(endDate.getDate())} ${endDate.toLocaleString('default', { month: 'short' })} ${endDate.getFullYear()}`;

        return `${start} - ${end}`;
    }

    return '';
}

function getOrdinalIndicator(day: number): string {
    const j = day % 10,
        k = day % 100;
    if (j == 1 && k != 11) {
        return "st";
    }
    if (j == 2 && k != 12) {
        return "nd";
    }
    if (j == 3 && k != 13) {
        return "rd";
    }
    return "th";
}

const CampaignListItem: React.FC<CampaignListItemProps> = ({ campaign, selectedIndex, index, handleClick/*, refreshCampaigns*/ }) => {
    const [showPostContentDialog, setShowPostContentDialog] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const dispatch = useDispatch<AppDispatch>();

    const handleCampaignDelete = () => {
        dispatch(deleteCampaign(campaign.campaignGuid))
            .then(() => {
                setShowDeleteDialog(false);
            })
            .catch(error => {
                console.error('Error deleting campaign', error);
                setShowDeleteDialog(false);
            });
    }

    return (
        <>
            <Card sx={{ mb: 2 }} key={index}>
                <CardContent>
                    <Grid container alignItems="center">
                        <Grid item xs={2}>
                            <Typography variant="h6">{campaign.name}</Typography>
                            <Typography variant="caption" color="textSecondary">{getLastUpdatedFormatted(campaign.lastUpdated)}</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Grid container alignItems="center">
                                <Icon icon="majesticons:calendar-line" />
                                <Typography variant="body2" sx={{ ml: 1 }}>{getFormattedDateRange(campaign.startDate, campaign.endDate)}</Typography>
                            </Grid>
                        </Grid>
                        <Grid item xs={2}>
                            <Grid container alignItems="center">
                                <Icon icon="mdi:clock-outline" />
                                <Typography variant="body2" sx={{ ml: 1 }}>{campaign.scheduledPosts} scheduled</Typography>
                            </Grid>
                        </Grid>
                        <Grid item xs={2}>
                            <Grid container alignItems="center">
                                <Icon icon="icon-park-solid:message-sent" />
                                <Typography variant="body2" sx={{ ml: 1 }}>{campaign.sentPosts} sent</Typography>
                            </Grid>
                        </Grid>
                        <Grid item xs={4} style={{ textAlign: 'right' }}>
                            <Button variant="contained" color="primary"
                                startIcon={<Icon icon="material-symbols:add" />}
                                sx={{ mr: 1 }}
                                onClick={() => setShowPostContentDialog(true)}
                            >
                                Create Post
                            </Button>
                            <SplitButton options={["View Campaign", "Delete Campaign"]}
                                selectedIndex={selectedIndex} handleClick={handleClick} index={index} onDelete={() => setShowDeleteDialog(!showDeleteDialog)} />
                        </Grid>
                    </Grid>

                </CardContent>
            </Card>

            {showPostContentDialog && <PostContentDialog
                show={showPostContentDialog}
                setShow={setShowPostContentDialog}
                campaign={campaign}
            />}

            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
                <DialogTitle>Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this campaign? Posts associated with this Campaign will not be deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteDialog(false)} color="primary">
                        CANCEL
                    </Button>
                    <Button onClick={handleCampaignDelete} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    )
}

export default CampaignListItem;